/**
 * The single CBAM cost calculation for portal product rows, shared by the
 * operator preview page, the public share page, and the xlsx/CSV exports.
 *
 * It existed as two byte-identical copies before (one per page), which is
 * exactly the drift risk the "operator preview == public payload" invariant
 * (D6) exists to prevent — the pages must price the same payload the same way.
 *
 * Nothing here derives emissions: `sefa`, `useDirect` and the EU defaults all
 * arrive precomputed on the row. The chargeable base is industry-dependent and
 * the backend already resolved which one applies — `useDirect` is true for Iron
 * & Steel / Aluminium (charged on Direct SEE), false for every other sector
 * (Total SEE). Then chargeable = max(0, basis − SEFA), and the premium is that
 * times the viewer's certificate price.
 *
 * @param {object} product  A row from the portal payload.
 * @param {number} priceNum The viewer's certificate price (€/tCO₂e).
 * @returns {{base:number|undefined, sefa:number|null, chargeable:number, cost:number, isDirectOnly:boolean}}
 */
export const computeRowCost = (product, priceNum) => {
  const isDirectOnly = Boolean(product?.useDirect);
  const base = isDirectOnly ? product?.direct : product?.total;
  const sefa = product?.sefa ?? null;
  // Clamped at 0: a SEFA above the basis means the free allocation covers the
  // whole product, never a negative premium.
  const chargeable = Math.max(0, (base ?? 0) - (sefa ?? 0));
  const cost = chargeable * (Number(priceNum) || 0);
  return { base, sefa, chargeable, cost, isDirectOnly };
};

/**
 * Stable identity for a portal product row. The payload carries no row id (it
 * is assembled per request), so the key is the tuple that makes a row unique
 * plus the index as a final tie-break.
 */
export const portalProductRowKey = (product, index) =>
  [
    product?.cnCode,
    product?.productName,
    product?.installationName,
    product?.reportingPeriod,
    product?.revisionNumber,
    index,
  ].join('|');
